import { type Capabilities, type StaffResource, levelRank } from "@/shared/permissions";

// Client-side access model for the admin portal. The server is the real authority
// (requireCapability on every /admin route); this mirrors it so we can hide/redirect/explain
// rather than show blank pages from 403'd fetches.

// A single resource, an any-of list (read on ANY of them grants access — used by the Scholen
// console which serves both scholen + inschrijvingen data), or the special "adminOnly"/"always".
export type AdminAccess = StaffResource | StaffResource[] | "adminOnly" | "always";

/** Can this staff member open a route guarded by `access`? Admin = full on everything. */
export function canAccess(access: AdminAccess, roles: string[], caps: Capabilities): boolean {
  if (access === "always") return true;
  const isAdmin = roles.includes("admin");
  if (access === "adminOnly") return isAdmin;
  if (Array.isArray(access)) return isAdmin || access.some((a) => levelRank(caps[a] ?? "none") >= 1);
  return isAdmin || levelRank(caps[access] ?? "none") >= 1;
}

// Ordered exactly like the sidebar (AdminNav NAV_GROUPS) so the landing matches the first
// visible nav item: standalone Scholen/Aanbod/Inschrijvingen, then Monitoring, then Instellingen.
const LANDING_ORDER: { path: string; access: AdminAccess }[] = [
  { path: "/admin/overzicht", access: "overzicht" },
  // The Scholen console serves both school-data and programme (inschrijvingen) datasets.
  { path: "/admin/scholen", access: ["scholen", "inschrijvingen"] },
  { path: "/admin/aanbod", access: "aanbod" },
  { path: "/admin/backups", access: "backups" },
  { path: "/admin/logs", access: "audit" },
  { path: "/admin/instellingen", access: "configuratie" },
];

/**
 * The path a staff member should land on from "/" or "/admin": their first accessible section,
 * falling back to their own profile when they have no read rights anywhere.
 */
export function firstAccessiblePath(roles: string[], caps: Capabilities): string {
  for (const e of LANDING_ORDER) if (canAccess(e.access, roles, caps)) return e.path;
  return "/admin/profiel";
}
